import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer } from 'react-leaflet';
import OsmTiles from '../../components/OsmTiles';
import { collection, getDocs, query, where, doc, getDoc, documentId } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CheckIn, User, DEFAULT_SETTINGS } from '../../types';
import { exportToCSV } from '../../utils/export';
import { haversine } from '../../utils/route';
import LeafletCluster, { ClusterPoint } from '../../components/LeafletCluster';
import { useAccessScope } from '../../hooks/useAccessScope';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCheckinsEscopado } from '../../services/checkinsQuery';
import { PAPEIS_FILTRAVEIS } from '../DashboardPage';
import { escHtml } from '../../utils/escapeHtml';
import 'leaflet/dist/leaflet.css';

// escHtml agora vem do util compartilhado (escapa também aspas). O TOOLTIP é escapado dentro do
// LeafletCluster; aqui escapamos os campos que entram no popupHtml.

// Paleta para diferenciar vendedores no mapa
const PALETTE = ['#f5821f', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#4f46e5', '#0d9488', '#9333ea'];

const chunk = <T,>(a: T[], n: number) => { const o: T[][] = []; for (let i = 0; i < a.length; i += n) o.push(a.slice(i, i + n)); return o; };

export default function CheckInsMapPage() {
  const { user } = useAuth();
  const scope = useAccessScope();
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(false);
  const [buscou, setBuscou] = useState(false);   // já rodou alguma consulta (mapa só aparece depois)
  const [filterClient, setFilterClient] = useState('');
  // SEM período padrão: o mapa começa vazio e só consulta depois que o usuário filtra
  // (vendedor e/ou período) — não baixa nada à toa ao abrir a tela.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientsLoc, setClientsLoc] = useState<Map<string, { lat: number; lng: number; name: string }>>(new Map());
  const [radius, setRadius] = useState(DEFAULT_SETTINGS.checkinRadiusMeters);
  // Filtro de vendedor PADRÃO (typeahead por código OU nome), igual Dashboard/Histórico
  const [sellers, setSellers] = useState<User[]>([]);
  const [filtroSellerId, setFiltroSellerId] = useState(''); // '' = todos
  const [sellerInput, setSellerInput] = useState('');
  const [sellerFocus, setSellerFocus] = useState(false);

  // Usuários do typeahead: TODOS os perfis de campo (seller + supervisor/coordenador/gerente/GN/
  // pricing/diretor — gestores também fazem check-in). Admin vê todos; gestor escopado só a equipe.
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        let arr = (snap.docs.map((d) => ({ id: d.id, ...d.data() })) as User[])
          .filter((u) => PAPEIS_FILTRAVEIS.has(u.role) && (u as any).active !== false);
        if (!scope.full) { const idSet = new Set(scope.sellerIds || []); arr = arr.filter((s) => idSet.has(s.id)); }
        arr.sort((a, b) => ((a.codVendSankhya ?? 1e9) as number) - ((b.codVendSankhya ?? 1e9) as number));
        setSellers(arr);
      } catch { setSellers([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Consulta SÓ quando há filtro (vendedor e/ou período) — no servidor: por sellerId (índice
  // checkins(sellerId,createdAt)) e/ou range de createdAt. Sem filtro = mapa vazio, zero download.
  useEffect(() => {
    if (!filtroSellerId && !dateFrom && !dateTo) { setCheckins([]); setBuscou(false); return; }
    const load = async () => {
      setLoading(true);
      try {
        // Helper escopado: acesso total consulta direto; GESTOR sem vendedor escolhido cai em lotes de
        // `sellerIds` (a regra recusa consulta ampla dele); vendedor só as próprias.
        const rows = await fetchCheckinsEscopado(user, scope, {
          sellerId: filtroSellerId || undefined,
          desde: dateFrom ? new Date(dateFrom + 'T00:00:00') : undefined,
          ate: dateTo ? new Date(dateTo + 'T23:59:59') : undefined,
          ordem: 'desc',
          ordenarNoServidor: true,
          limitTotal: 2000,
        });
        const data = rows.map((raw: any) => ({
          ...raw,
          createdAt: raw.createdAt?.toDate?.() ?? new Date(),
          checkoutAt: raw.checkoutAt?.toDate?.() ?? null,
        })) as CheckIn[];
        setCheckins(data);
        setBuscou(true);
      } catch (e) {
        console.warn('Check-ins no mapa: erro ao consultar', e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroSellerId, dateFrom, dateTo, user, scope]);

  // Raio configurado (1 doc, barato) — carrega uma vez.
  useEffect(() => {
    getDoc(doc(db, 'settings', 'app')).then((s) => {
      if (s.exists()) setRadius(Number((s.data() as any).checkinRadiusMeters) || DEFAULT_SETTINGS.checkinRadiusMeters);
    }).catch(() => {});
  }, []);

  // Coordenadas SÓ dos clientes das visitas carregadas (por documentId em lotes de 30) — antes
  // baixava a coleção `clients` INTEIRA (~15k docs) só p/ medir distância.
  useEffect(() => {
    const ids = Array.from(new Set(checkins.map((c) => c.clientId).filter(Boolean)));
    const faltam = ids.filter((id) => !clientsLoc.has(id));
    if (!faltam.length) return;
    (async () => {
      try {
        const snaps = await Promise.all(
          chunk(faltam, 30).map((c) => getDocs(query(collection(db, 'clients'), where(documentId(), 'in', c))))
        );
        setClientsLoc((prev) => {
          const m = new Map(prev);
          snaps.forEach((sn) => sn.forEach((d) => {
            const c = d.data();
            if (c.location?.latitude && c.location?.longitude) m.set(d.id, { lat: c.location.latitude, lng: c.location.longitude, name: c.name });
          }));
          return m;
        });
      } catch { /* distância fica como "sem coordenada" */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkins]);

  // Typeahead local (código OU nome) — mesmo padrão do Dashboard/Histórico.
  const vendLabel = (s: User) => `${s.codVendSankhya ? `${s.codVendSankhya} - ` : ''}${s.name}`;
  const vendedorSel = sellers.find((s) => s.id === filtroSellerId);
  const committedLabel = vendedorSel ? vendLabel(vendedorSel) : '';
  const buscaVend = sellerInput.trim().toLowerCase();
  const sellerOpcoes = (!buscaVend || sellerInput === committedLabel) ? [] : sellers.filter((s) =>
    String(s.codVendSankhya ?? '').includes(buscaVend) || (s.name || '').toLowerCase().includes(buscaVend)
  ).slice(0, 30);
  const selecionarVendedor = (s: User) => { setFiltroSellerId(s.id); setSellerInput(vendLabel(s)); setSellerFocus(false); };
  const limparVendedor = () => { setFiltroSellerId(''); setSellerInput(''); };

  // Gestor: restringe aos check-ins dos vendedores da hierarquia dele.
  const scoped = useMemo(() => (scope.full ? checkins : checkins.filter(c => (scope.sellerIds || []).includes(c.sellerId))), [checkins, scope]);

  // Cor por vendedor (sellerId) + legenda
  const sellerColor = useMemo(() => {
    const ids = Array.from(new Set(scoped.map((c) => c.sellerId).filter(Boolean)));
    const m = new Map<string, string>();
    ids.forEach((id, i) => m.set(id, PALETTE[i % PALETTE.length]));
    return m;
  }, [scoped]);

  // Vendedor e período já são aplicados na consulta ao servidor; aqui só o filtro de cliente (texto).
  const filtered = useMemo(() => scoped.filter(c => {
    if (filterClient && !c.clientName.toLowerCase().includes(filterClient.toLowerCase())) return false;
    return true;
  }), [scoped, filterClient]);

  const fmtDistKm = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

  // Pontos do mapa (memoizados, agrupados em cluster)
  const points = useMemo<ClusterPoint[]>(() => filtered.map((ci) => {
    const color = sellerColor.get(ci.sellerId) || '#f5821f';
    const cl = clientsLoc.get(ci.clientId);
    const dist = cl ? haversine({ lat: ci.location.latitude, lng: ci.location.longitude }, cl) : null;
    const fmtD = (d: Date) => d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const distLine = dist != null
      ? `<span style="color:${dist <= radius ? '#16a34a' : '#dc2626'};font-weight:600">Distância do cliente: ${fmtDistKm(dist)} ${dist <= radius ? '✓ no local' : '⚠️ distante'}</span>`
      : `<span style="color:#999">Cliente sem coordenada</span>`;
    const popupHtml =
      `<strong>${escHtml(ci.clientName)}</strong><br/>` +
      `Vendedor: ${escHtml(ci.sellerName)}<br/>` +
      `Check-in: ${escHtml(fmtD(ci.createdAt))}<br/>` +
      (ci.checkoutAt ? `Check-out: ${escHtml(fmtD(ci.checkoutAt))}<br/>` : '') +
      distLine +
      ((ci.reasons && ci.reasons.length) ? `<br/>Motivos: ${escHtml(ci.reasons.join(', '))}` : '') +
      (ci.notes ? `<br/><em>${escHtml(ci.notes)}</em>` : '');
    return {
      id: ci.id, lat: ci.location.latitude, lng: ci.location.longitude,
      color, radius: 8, strokeColor: '#fff', weight: 2,
      tooltip: ci.sellerName, tooltipPermanent: false, popupHtml,
    } as ClusterPoint;
  }), [filtered, sellerColor, clientsLoc, radius]);

  // Vendedores presentes no resultado filtrado, com sua cor (para a legenda)
  const legendSellers = useMemo(() => {
    const seen = new Map<string, string>();
    filtered.forEach((c) => { if (c.sellerId && !seen.has(c.sellerId)) seen.set(c.sellerId, c.sellerName); });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name, color: sellerColor.get(id) || '#f5821f' }));
  }, [filtered, sellerColor]);

  const formatDate = (d: Date) => d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleExportExcel = () => {
    const rows = filtered.map(ci => ({
      Data: formatDate(ci.createdAt),
      CheckOut: ci.checkoutAt ? formatDate(ci.checkoutAt) : '',
      Vendedor: ci.sellerName,
      Cliente: ci.clientName,
      Motivos: (ci.reasons || []).join(', '),
      Observacao: ci.notes || '',
      Latitude: ci.location.latitude,
      Longitude: ci.location.longitude,
    }));
    exportToCSV(rows, `checkins-${new Date().toISOString().slice(0, 10)}`);
  };

  const center: [number, number] = filtered.length > 0
    ? [filtered[0].location.latitude, filtered[0].location.longitude]
    : [-15.7801, -47.9292];

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>Check-ins no Mapa</h1>
          <p style={styles.subtitle}>
            {buscou ? `${filtered.length} visitas encontradas` : 'Filtre por vendedor e/ou período para carregar as visitas'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={styles.exportExcel} onClick={handleExportExcel}>⬇️ Excel</button>
          <button style={styles.exportPdf} onClick={() => window.print()}>🖨️ PDF</button>
        </div>
      </div>

      <div style={styles.filters}>
        <div style={styles.filterItem}>
          <label style={styles.filterLabel}>De</label>
          <input type="date" style={styles.input} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div style={styles.filterItem}>
          <label style={styles.filterLabel}>Até</label>
          <input type="date" style={styles.input} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        <div style={styles.filterItem}>
          <label style={styles.filterLabel}>Vendedor</label>
          <div style={{ position: 'relative', minWidth: 260 }}>
            <input
              style={{ ...styles.input, width: '100%', boxSizing: 'border-box', paddingRight: 30 }}
              placeholder="Buscar por código ou nome…"
              value={sellerInput}
              onChange={e => setSellerInput(e.target.value)}
              onFocus={() => setSellerFocus(true)}
              onBlur={() => setSellerFocus(false)}
              autoComplete="off"
            />
            {(sellerInput || filtroSellerId) && (
              <button type="button" style={styles.filterClear} title="Limpar" onMouseDown={(e) => { e.preventDefault(); limparVendedor(); }}>✕</button>
            )}
            {sellerFocus && sellerOpcoes.length > 0 && (
              <div style={styles.filterDropdown}>
                {sellerOpcoes.map((s) => (
                  <div key={s.id} style={styles.filterOption} onMouseDown={(e) => { e.preventDefault(); selecionarVendedor(s); }}>
                    {vendLabel(s)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ ...styles.filterItem, flex: 1 }}>
          <label style={styles.filterLabel}>Cliente</label>
          <input style={styles.input} placeholder="Filtrar por cliente..." value={filterClient} onChange={e => setFilterClient(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <p>Carregando check-ins...</p>
      ) : !buscou ? (
        <div style={styles.placeholder}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🗺️</div>
          <p style={{ margin: 0, fontWeight: 600, color: '#555' }}>O mapa carrega após aplicar um filtro</p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#888' }}>
            Escolha um vendedor (código ou nome) e/ou um período acima — assim só baixamos as visitas necessárias.
          </p>
        </div>
      ) : (
        <div style={styles.mapWrapper}>
          {legendSellers.length > 0 && (
            <div style={styles.legend}>
              <div style={styles.legendTitle}>Vendedores</div>
              {legendSellers.map((s) => (
                <div key={s.id} style={styles.legendItem}>
                  <span style={{ ...styles.legendDot, background: s.color }} />{s.name}
                </div>
              ))}
            </div>
          )}
          <MapContainer center={center} zoom={filtered.length > 0 ? 12 : 4} style={{ height: '600px', width: '100%', borderRadius: 12 }}>
            <OsmTiles />
            <LeafletCluster points={points} />
          </MapContainer>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  exportExcel: { background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  exportPdf: { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  filters: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' },
  filterItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 12, fontWeight: 600, color: '#666' },
  input: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', fontSize: 14, background: '#fff' },
  filterClear: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#999', fontSize: 15, cursor: 'pointer', padding: 4, lineHeight: 1 },
  filterDropdown: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 700, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto' },
  filterOption: { padding: '10px 12px', fontSize: 14, color: '#333', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' },
  placeholder: { background: '#fff', borderRadius: 12, boxShadow: '0 1px 12px rgba(0,0,0,0.08)', padding: '60px 24px', textAlign: 'center' },
  mapWrapper: { borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 12px rgba(0,0,0,0.08)', position: 'relative' },
  legend: { position: 'absolute', top: 10, right: 10, zIndex: 500, background: '#fff', borderRadius: 8, padding: '8px 12px', boxShadow: '0 1px 6px rgba(0,0,0,0.2)', maxHeight: 220, overflowY: 'auto', maxWidth: 220 },
  legendTitle: { fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 6, textTransform: 'uppercase' },
  legendItem: { display: 'flex', alignItems: 'center', fontSize: 12, color: '#333', marginBottom: 3 },
  legendDot: { display: 'inline-block', width: 11, height: 11, borderRadius: 6, marginRight: 7, flexShrink: 0 },
};
